import {
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { useOnceMutation } from './once-mutation';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import type { Envelope } from './envelope';
import { iamKeys, type IamTeam } from './iam/types';

const foundationApi = getHttpClient('foundationApi');

// ============================================
// Types  (ported from legacy @console v3 service)
// ============================================

export type TeamRole = 'owner' | 'member';
export type MemberStatus = 'active' | 'pending' | 'inactive';

export interface Team {
  id: string;
  team_name: string;
  team_description: string;
  team_type: string;
  team_slug: string;
  organization: string;
  active: boolean;
  owner: string;
  members_count?: number;
  shares_count?: number;
  created_at: string;
  updated_at: string;
  /** Current user's role in this team — derived from the list endpoint. */
  user_role?: TeamRole;
}

export interface Member {
  id: string;
  email: string;
  uid?: string;
  active: boolean;
  pending: boolean;
  /** Deny-by-default: member sees only resources explicitly shared with them. */
  restricted: boolean;
  /** Per-member accept link token. Owner-only (redacted for other members); set while pending. */
  invite_token?: string;
  roles: string[];
  permissions?: string[];
  invited_at?: string;
  joined_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTeamRequest {
  team_name: string;
  team_description?: string;
  team_type?: string;
}

export interface UpdateTeamRequest {
  team_name?: string;
  team_description?: string;
  team_type?: string;
  active?: boolean;
}

export type PermissionAction = 'all' | 'list' | 'show' | 'create' | 'edit' | 'delete' | 'report';

export const PERMISSION_ACTIONS: {
  key: PermissionAction;
  label: string;
  description: string;
}[] = [
  { key: 'all', label: 'Full Access', description: 'Complete control over all operations' },
  { key: 'list', label: 'List', description: 'View the list of resources' },
  { key: 'show', label: 'View', description: 'View resource details' },
  { key: 'create', label: 'Create', description: 'Create new resources' },
  { key: 'edit', label: 'Edit', description: 'Modify existing resources' },
  { key: 'delete', label: 'Delete', description: 'Remove resources' },
  // Exclusive read-only role: restricts the member to overview, analytics and
  // usage reports only — no domains, settings, or any management/write access.
  { key: 'report', label: 'Report (read-only)', description: 'View overview, analytics & usage only — no management' },
];

/** A team as the IAM API returns it, in the shape the pages already use. */
export function iamTeamToTeam(t: IamTeam): Team {
  return {
    id: t.id,
    team_name: t.name,
    team_description: t.description,
    team_type: '',
    team_slug: t.slug,
    organization: t.org_id,
    active: t.status === 'active',
    owner: '',
    members_count: t.member_count,
    created_at: t.created_at,
    updated_at: t.updated_at,
  };
}

// ============================================
// Query keys
// ============================================

export const teamKeys = {
  all: ['v3', 'teams'] as const,
  team: (id: string) => ['v3', 'teams', id] as const,
  members: (id: string) => ['v3', 'teams', id, 'members'] as const,
  shares: (id: string) => ['v3', 'teams', id, 'shares'] as const,
  invitationLink: (id: string) => ['v3', 'teams', id, 'invitation'] as const,
  myInvitations: ['v3', 'me', 'invitations'] as const,
};

export interface PendingInvitation {
  id: string;
  team_id: string;
  team_name: string;
  team_owner?: string;
  roles?: string[];
  invited_at?: string | null;
}

// ============================================
// Invitation detail types (GET /me/invitations/:member_id)
// ============================================

export interface ServicePermission {
  service_level?: string[];
  resources?: Record<string, {
    type_level?: string[];
    specific?: Record<string, string[]>;
  }>;
}

export interface MemberPermissionTree {
  member_id: string;
  permissions: Record<string, ServicePermission>;
}

export interface InvitationDetail {
  /** Same as member_id. */
  id: string;
  email: string;
  team_id: string;
  team_name: string;
  team_owner?: string;
  roles?: string[];
  permissions?: MemberPermissionTree;
  service_names?: Record<string, string>;
}

/** Response shape from POST /teams/validate. */
export interface TokenValidation {
  valid: boolean;
  team_name: string;
  team_id: string;
  owner: string;
  member?: { id: string; email: string; pending: boolean };
  message: string;
}

// ============================================
// Query hooks
// ============================================

/** The current user's pending team invitations (across orgs). */
export function useMyInvitations() {
  return useQuery<PendingInvitation[]>({
    queryKey: teamKeys.myInvitations,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<{ invitations?: PendingInvitation[] }>>(
        '/me/invitations',
      );
      return res.data?.data?.invitations ?? [];
    },
  });
}

/** Fetch the full invitation detail for the current user. `enabled` when memberId is set. */
export function useInvitationDetail(memberId?: string) {
  return useQuery({
    queryKey: ['v3', 'me', 'invitations', memberId ?? ''],
    enabled: !!memberId,
    retry: false,
    queryFn: async (): Promise<InvitationDetail> => {
      const res = await foundationApi.get<Envelope<InvitationDetail>>(
        `/me/invitations/${memberId}`,
      );
      return res.data.data;
    },
  });
}

export function useTeam(id: string | undefined) {
  return useQuery({
    queryKey: teamKeys.team(id ?? ''),
    enabled: !!id,
    retry: false,
    queryFn: async (): Promise<Team | null> => {
      try {
        const res = await foundationApi.get<Envelope<IamTeam>>(`/iam/teams/${id}`);
        return res.data?.data ? iamTeamToTeam(res.data.data) : null;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // No access / not found are handled gracefully in the UI.
        if (status === 403 || status === 404) return null;
        throw error;
      }
    },
  });
}

// ============================================
// Mutation hooks
// ============================================

/** Invalidate the IAM team list + a single team's caches. */
function useTeamInvalidation() {
  const qc = useQueryClient();
  return {
    list: () => qc.invalidateQueries({ queryKey: iamKeys.teamList }),
    team: (id: string) => qc.invalidateQueries({ queryKey: teamKeys.team(id) }),
    members: (id: string) => qc.invalidateQueries({ queryKey: teamKeys.members(id) }),
    shares: (id: string) => qc.invalidateQueries({ queryKey: teamKeys.shares(id) }),
    invitationLink: (id: string) =>
      qc.invalidateQueries({ queryKey: teamKeys.invitationLink(id) }),
  };
}

export function useCreateTeam(
  options?: UseMutationOptions<Team, unknown, CreateTeamRequest>,
) {
  const inv = useTeamInvalidation();
  return useOnceMutation({
    mutationFn: async (body: CreateTeamRequest) => {
      const res = await foundationApi.post<Envelope<IamTeam>>('/iam/teams', {
        name: body.team_name,
        description: body.team_description ?? '',
      });
      return iamTeamToTeam(res.data.data);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

export function useUpdateTeam(
  id: string,
  options?: UseMutationOptions<Team, unknown, UpdateTeamRequest>,
) {
  const inv = useTeamInvalidation();
  return useOnceMutation({
    mutationFn: async (body: UpdateTeamRequest) => {
      const res = await foundationApi.patch<Envelope<IamTeam>>(`/iam/teams/${id}`, {
        name: body.team_name,
        description: body.team_description,
        status: body.active === undefined ? undefined : body.active ? 'active' : 'archived',
      });
      return iamTeamToTeam(res.data.data);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      inv.team(id);
      options?.onSuccess?.(...args);
    },
  }, { keyExtra: [id] });
}

export function useDeleteTeam(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const inv = useTeamInvalidation();
  return useOnceMutation({
    mutationFn: async (id: string) => {
      await foundationApi.delete(`/iam/teams/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      inv.list();
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Invitation accept flow
// ============================================

/**
 * Validate an invitation token (POST /teams/validate).
 * Returns token validity + team info; any API error is treated as invalid.
 */
export function useValidateToken(token: string | undefined) {
  return useQuery({
    queryKey: ['v3', 'teams', 'validate', token ?? ''],
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
    queryFn: async (): Promise<TokenValidation> => {
      try {
        const res = await foundationApi.post<Envelope<TokenValidation>>('/teams/validate', { token });
        return res.data.data;
      } catch {
        return {
          valid: false,
          team_name: '',
          team_id: '',
          owner: '',
          message: 'Invalid or expired invitation.',
        };
      }
    },
  });
}

/** Accept an invitation for the currently logged-in user. Body: `{token}` or `{member_id}`. */
export function useAcceptInvitation(
  options?: UseMutationOptions<unknown, unknown, { token?: string; member_id?: string }>,
) {
  const qc = useQueryClient();
  return useOnceMutation({
    mutationFn: async (body: { token?: string; member_id?: string }) => {
      const res = await foundationApi.post('/teams/accepted', body);
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: teamKeys.myInvitations });
      qc.invalidateQueries({ queryKey: iamKeys.teamList });
      options?.onSuccess?.(...args);
    },
  });
}

/** Join a team as a newly registered user. Body: `{token, email, uid}`. */
export function useJoinTeam(
  options?: UseMutationOptions<unknown, unknown, { token: string; email: string; uid: string }>,
) {
  const qc = useQueryClient();
  return useOnceMutation({
    mutationFn: async (body: { token: string; email: string; uid: string }) => {
      const res = await foundationApi.post('/teams/join', body);
      return res.data?.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: teamKeys.myInvitations });
      qc.invalidateQueries({ queryKey: iamKeys.teamList });
      options?.onSuccess?.(...args);
    },
  });
}
